import { Component, OnInit, AfterViewInit, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { HeaderUserComponent } from '../../header-user/header-user.component';
import { AuthenticationService } from '../../../services/authentication.service';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotificationService } from '../../../services/notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SessionStorageService } from '../../../services/session-storage.service';

import { RouterModule } from '@angular/router';
import { GalaxyButtonDirective } from '../../../directives/galaxy-button.directive';
import { GalaxyRippleDirective } from '../../../directives/galaxy-ripple.directive';
import { GalaxyCardComponent } from '@app/components/ui/galaxy-card/galaxy-card.component';
import { InteractiveCrystalDirective } from '@app/directives/interactive-crystal.directive';

declare var google: any;

@Component({
  selector: 'app-registration',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    GalaxyButtonDirective,
    GalaxyRippleDirective,
    GalaxyCardComponent,
    InteractiveCrystalDirective,
  ],
  templateUrl: './registration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './registration.component.css',
})
export class RegistrationComponent implements OnInit, AfterViewInit {
  registrationForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authenticationService: AuthenticationService,
    private sessionStorage: SessionStorageService,
    private router: Router,
    private notificationService: NotificationService,
    private ngZone: NgZone
  ) {
    this.registrationForm = this.fb.group({
      username: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      address: ['', Validators.required],
      cellphone: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(6)]],
      userType: ['USER'], // Default value
    });
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.initializeGoogleSignIn();
  }

  private initializeGoogleSignIn(): void {
    if (typeof google !== 'undefined' && google.accounts) {
      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: this.handleGoogleCredentialResponse.bind(this)
      });
      google.accounts.id.renderButton(
        document.getElementById('google-btn-register-container'),
        { theme: 'outline', size: 'large', width: '100%', shape: 'pill' } 
      );
    }
  }

  handleGoogleCredentialResponse(response: any): void {
    if (response.credential) {
      this.ngZone.run(() => {
        this.authenticationService.googleLogin(response.credential).subscribe({
          next: (token) => {
            this.sessionStorage.setItem('token', token);
            this.notificationService.showSuccess('Registro exitoso', '¡Bienvenido con Google!');
            this.router.navigate(['/']);
          },
          error: (err) => {
            this.notificationService.showError('Error', err.error?.message || 'Error al iniciar sesión con Google');
          }
        });
      });
    }
  }

  register(): void {
    if (this.registrationForm.invalid) {
      this.notificationService.showInfo(
        'Formulario inválido',
        'Por favor, completa todos los campos requeridos y válidos.',
      );
      return;
    }

    localStorage.removeItem('token'); // Clear token before new registration

    const user = this.registrationForm.value;

    this.authenticationService.register(user).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          'Registro exitoso',
          'Usuario registrado correctamente. Ahora puedes iniciar sesión.',
        );
        this.router.navigate(['user/login']);
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 409) {
          this.notificationService.showWarning(
            'Este correo electrónico ya está registrado.',
            'Error de registro',
          );
        } else {
          this.notificationService.showError(
            'Ocurrió un error inesperado al registrar el usuario.',
            'Error de registro',
          );
        }
      },
    });
  }

  get f() {
    return this.registrationForm.controls;
  }
}
